import { ButtonRowRight, Headline5 } from 'components/Elements';
import strings from 'strings/strings.json';
import React from 'react';
import RadioFields, { RadioFieldOption } from 'components/forms/RadioFields';
import useNeveForm from 'components/forms/NeveForm';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import styled from 'styled-components';
import { sizes } from 'theme';
import { Component } from './BuildCourseSwitch';
import Button from 'components/buttons/Button';

enum BuildCourseMethod {
    MANUAL = 0,
    SCORM = 1,
    POWERPOINT = 2,
}

type CreateCourseOption = {
    type: BuildCourseMethod;
    name: string;
    description: string;
    aria: string;
};

type CreateCourseMethodInput = {
    type: 0 | 1 | null;
};

type SelectCreateCourseMethodProps = {
    setComponent: React.Dispatch<React.SetStateAction<Component>>;
};

export default function SelectCreateCourseMethodForm({ setComponent }: SelectCreateCourseMethodProps): JSX.Element {
    const [error, setError] = React.useState<boolean>(false);

    const { register, handleSubmit } = useNeveForm<CreateCourseMethodInput>({
        type: null,
    });

    const options: CreateCourseOption[] = [
        {
            type: BuildCourseMethod.MANUAL,
            name: strings.createCourseModal.createCourseMethod.manual.name,
            description: '',
            aria: strings.createCourseModal.createCourseMethod.manual.aria,
        },
        {
            type: BuildCourseMethod.POWERPOINT,
            name: strings.createCourseModal.createCourseMethod.powerPoint.name,
            description: '',
            aria: strings.createCourseModal.createCourseMethod.powerPoint.aria,
        },
        {
            type: BuildCourseMethod.SCORM,
            name: strings.createCourseModal.createCourseMethod.scorm.name,
            description: '',
            aria: strings.createCourseModal.createCourseMethod.scorm.aria,
        },
    ];

    const radioFieldOptions: RadioFieldOption[] = options.map((option) => {
        return {
            value: option.type,
            title: option.name,
            description: '',
            aria: option.aria,
            inputProps: register('type'),
            name: 'type',
        };
    });

    const submit = (formData: CreateCourseMethodInput) => {
        setError(false);

        if (formData.type === null) {
            setError(true);
            return;
        }

        const selection = options.find((x) => +x.type === +formData.type!);
        switch (selection!.type) {
            case BuildCourseMethod.MANUAL:
                setComponent('createCourseManual');
                break;
            case BuildCourseMethod.POWERPOINT:
                setComponent('createCoursePowerPoint');
                break;
            case BuildCourseMethod.SCORM:
                setComponent('createCourseSCORM');
                break;
        }
    };

    return (
        <FormContainer onSubmit={handleSubmit(submit)}>
            <Headline5>{strings.createCourseModal.createCourseMethod.title}</Headline5>
            <RadioFields options={radioFieldOptions} autoFocus />
            <ButtonRowRight>
                {error && <ErrorNotifierSlim description={strings.createCourseModal.createCourseMethod.error} />}
                <Button
                    type="submit"
                    title={strings.createCourseModal.createCourseMethod.continueButton.title}
                    aria={strings.createCourseModal.createCourseMethod.continueButton.aria}
                />
            </ButtonRowRight>
        </FormContainer>
    );
}

const FormContainer = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;
